/**
 * The SVG renderer: a function of the scene, and nothing else.
 *
 * It draws what `buildScene` produced and makes no decisions about the design —
 * no positions are computed here beyond laying out labels and badges around
 * positions the scene already resolved. Interaction lives in the editor
 * reducer (design §1.7); this component attaches no handlers. It marks each
 * drawn element with `data-kind` and `data-ref`, so the canvas can find what a
 * pointer event hit by delegation without the renderer knowing it happened.
 *
 * Presentation state that is not the scene — selection, highlighted connection
 * targets, the viewport — comes in as props, so selecting something does not
 * rebuild the scene and zoom never touches stored coordinates (requirement
 * 12.8).
 *
 * Each element is a memoised component keyed on its scene object. The scene
 * builder preserves identity for untouched elements, so an edit re-renders what
 * it changed rather than the whole schematic.
 */
import { memo, useId, useMemo, type ReactElement } from "react";
import type { LabelSide, Severity } from "../design/document";
import { AIR } from "../design/protocol";
import type { StepPreview } from "../scripts/preview";
import type {
  Badged,
  Point,
  Scene,
  SceneBlock,
  SceneEdge,
  SceneNode,
  SceneNote,
  ScenePort,
  Size,
} from "../scene/types";
import { NOTE_METRICS } from "../scene/notes";
import { blocks, limitText, nominalVolumeLabel, portText, readoutText, regulatorText, volumeLabel } from "../scripts/matrix";
import { instrumentTag } from "../scripts/preview";
import { Glyph, MixingMark, SerpentineMark, switchedRoute } from "./glyphs";
import { SEVERITY_STYLE, THEME } from "./theme";

/** The scene point at the top-left of the view, and the scale applied to it. */
export interface Viewport {
  x: number;
  y: number;
  zoom: number;
}

export const DEFAULT_VIEWPORT: Viewport = { x: 0, y: 0, zoom: 1 };

const NO_REFS: ReadonlySet<string> = new Set();

/** Width, in screen pixels, of the invisible band that makes a line clickable. */
const HIT_WIDTH = 12;

/** Baseline of a node label below the symbol's extent. */
export const LABEL_OFFSET = 16;

/**
 * Where a node's label goes: its anchor point and alignment, for the side
 * chosen (screen terms, so it stays put as the symbol turns). Clears the
 * selection outline as well as the symbol. Shared with the canvas's in-place
 * label editor, so editing happens where the label is.
 */
export function labelPlacement(
  centre: Point,
  size: Size,
  rotation: number,
  side: LabelSide,
): { x: number; y: number; anchor: "start" | "middle" | "end" } {
  const extent = halfExtent(size, rotation);
  const gap = 8;
  switch (side) {
    case "above":
      return { x: centre.x, y: centre.y - extent.height - gap, anchor: "middle" };
    case "left":
      return { x: centre.x - extent.width - gap, y: centre.y + 4, anchor: "end" };
    case "right":
      return { x: centre.x + extent.width + gap, y: centre.y + 4, anchor: "start" };
    default:
      return { x: centre.x, y: centre.y + extent.height + LABEL_OFFSET, anchor: "middle" };
  }
}

export interface SvgRendererProps {
  scene: Scene;
  width: number;
  height: number;
  viewport?: Viewport;
  /** Refs of selected nodes, edges and blocks. */
  selection?: ReadonlySet<string>;
  /** Port refs to highlight as valid connection targets (D21). */
  highlightedPorts?: ReadonlySet<string>;
  /**
   * Node and edge refs to pick out, such as the parts on a hovered bill of
   * materials line. Presentation only: an export never passes it.
   */
  spotlight?: ReadonlySet<string>;
  /** Accessible name for the drawing. */
  title?: string;
  /** Prefix for SVG ids; defaults to a unique one so two renderers can coexist. */
  idPrefix?: string;
}

export function SvgRenderer({
  scene,
  width,
  height,
  viewport = DEFAULT_VIEWPORT,
  selection = NO_REFS,
  highlightedPorts = NO_REFS,
  spotlight = NO_REFS,
  title = "Schematic",
  idPrefix,
}: SvgRendererProps) {
  const generated = useId();
  const prefix = (idPrefix ?? `schematic-${generated}`).replace(/[^A-Za-z0-9_-]/g, "");
  const gridId = `${prefix}-grid`;
  const noteShadowId = `${prefix}-note-shadow`;
  const { zoom } = viewport;
  // Ports some line ends at: a junction draws those without their circle.
  const connected = useMemo(
    () => new Set(scene.edges.flatMap((edge) => [edge.from.ref, edge.to.ref])),
    [scene.edges],
  );
  // What each note's leader points at. Unchanged nodes keep their objects, so
  // a memoised note re-renders only when its own component moves.
  const nodesByRef = useMemo(() => new Map(scene.nodes.map((node) => [node.ref, node])), [scene.nodes]);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="group"
      aria-roledescription="schematic"
      aria-label={title}
      fontFamily={THEME.fontFamily}
      data-zoom={zoom}
    >
      <title>{title}</title>
      <defs>
        <pattern
          id={gridId}
          width={scene.grid.spacing}
          height={scene.grid.spacing}
          patternUnits="userSpaceOnUse"
        >
          <circle cx={0} cy={0} r={0.9 / zoom} fill={THEME.line} />
        </pattern>
        {/* A note's card lifts a little off the drawing: a subtle shadow, in the file too. */}
        <filter id={noteShadowId} x="-10%" y="-10%" width="120%" height="140%" colorInterpolationFilters="sRGB">
          <feDropShadow dx={0} dy={1} stdDeviation={1.5} floodColor={THEME.ink} floodOpacity={0.14} />
        </filter>
      </defs>
      {/* Zoom and pan are one transform over the scene, never a change to it. */}
      <g transform={`scale(${zoom}) translate(${-viewport.x} ${-viewport.y})`} data-layer="scene">
        {scene.grid.visible && (
          <rect
            data-layer="grid"
            x={viewport.x}
            y={viewport.y}
            width={width / zoom}
            height={height / zoom}
            fill={`url(#${gridId})`}
          />
        )}
        <g data-layer="frames">
          {scene.blocks
            .filter((block) => !block.collapsed)
            .map((block) => (
              <BlockFrame key={block.ref} block={block} selected={selection.has(block.ref)} />
            ))}
        </g>
        {/* Under everything: the wash over one side of the design's external
            interfaces, inside them or outside, so that side stands out. */}
        {scene.washed.size > 0 && <Wash scene={scene} />}
        {scene.preview && <PreviewBands scene={scene} />}
        {spotlight.size > 0 && <Spotlight scene={scene} refs={spotlight} />}
        <g data-layer="edges">
          {/* The tube through a hidden clamp-on part: drawn as its tubes are, never hit. */}
          {scene.tubeRuns.map((run) => (
            <line
              key={run.ref}
              data-tube-run={run.ref}
              x1={run.from.x}
              y1={run.from.y}
              x2={run.to.x}
              y2={run.to.y}
              stroke={THEME.channel}
              strokeWidth={THEME.edgeWidth}
              strokeDasharray={run.air ? "0.1 4" : undefined}
              strokeLinecap={run.air ? "round" : undefined}
              pointerEvents="none"
            />
          ))}
          {scene.edges.map((edge) => (
            <EdgeView
              key={edge.ref}
              edge={edge}
              selected={selection.has(edge.ref)}
              showDimension={scene.annotations.dimensions}
            />
          ))}
        </g>
        <g data-layer="nodes">
          {scene.nodes.map((node) => (
            <NodeView
              showLabel={scene.annotations.labels}
              key={node.ref}
              node={node}
              selected={selection.has(node.ref)}
              highlighted={highlightedAmong(node.ports, highlightedPorts)}
              quiet={node.glyph === "junction" ? connectedAmong(node.ports, connected) : ""}
              filled={Boolean(
                scene.preview?.nodes[node.ref]?.liquid && scene.preview.capacities?.[node.ref] && scene.preview.volumes[node.ref] !== undefined,
              )}
            />
          ))}
        </g>
        <g data-layer="blocks">
          {scene.blocks
            .filter((block) => block.collapsed)
            .map((block) => (
              <CollapsedBlockView
                key={block.ref}
                block={block}
                selected={selection.has(block.ref)}
                highlighted={highlightedAmong(block.ports, highlightedPorts)}
              />
            ))}
        </g>
        {scene.preview && <PreviewStates scene={scene} />}
        {/* On top: a note is read over whatever it sits on. */}
        <g data-layer="notes">
          {scene.notes.map((note) => (
            <NoteView
              key={note.ref}
              note={note}
              selected={selection.has(note.ref)}
              shadow={noteShadowId}
              anchor={note.anchor ? nodesByRef.get(note.anchor) : undefined}
            />
          ))}
        </g>
      </g>
    </svg>
  );
}

/**
 * Where a leader from `from` towards a symbol's centre meets the symbol's
 * box, so the dashes stop at the symbol rather than running across it. Null
 * when `from` is inside the box: there is nothing to draw between them.
 */
export function leaderEnd(from: Point, node: Pick<SceneNode, "centre" | "size" | "rotation">): Point | null {
  const extent = halfExtent(node.size, node.rotation);
  const dx = from.x - node.centre.x;
  const dy = from.y - node.centre.y;
  // How far along centre → from the box edge is, as a fraction of the way.
  const t = Math.min(
    dx === 0 ? Infinity : extent.width / Math.abs(dx),
    dy === 0 ? Infinity : extent.height / Math.abs(dy),
  );
  if (!(t < 1)) return null;
  return { x: node.centre.x + dx * t, y: node.centre.y + dy * t };
}

/**
 * A note: its text on a pale card. An attached note has a dashed leader to
 * the component it moves with, whenever notes are shown, on screen and in an
 * export; selected, the card is outlined and the leader takes the accent.
 */
const NoteView = memo(function NoteView({
  note,
  selected,
  anchor,
  shadow,
}: {
  note: SceneNote;
  selected: boolean;
  anchor?: SceneNode;
  /** The id of the card's drop shadow filter. */
  shadow: string;
}) {
  const { fontSize, lineHeight, padding, border } = NOTE_METRICS;
  // Where the editor puts its text: inside border and padding, each line
  // centred in its line height.
  const inset = padding + border;
  const baseline = inset + (lineHeight - fontSize) / 2 + fontSize * 0.8;
  const { x, y } = note.position;
  const { width, height } = note.size;
  const centre = { x: x + width / 2, y: y + height / 2 };
  // From the card's centre, under the card, so only the part outside it shows.
  const end = anchor ? leaderEnd(centre, anchor) : null;
  return (
    <g data-kind="note" data-ref={note.ref} data-selected={selected || undefined}>
      {end && (
        <line
          data-leader=""
          x1={centre.x}
          y1={centre.y}
          x2={end.x}
          y2={end.y}
          stroke={selected ? THEME.accent : THEME.noteEdge}
          strokeWidth={1}
          strokeDasharray="3 3"
        />
      )}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={3}
        fill={THEME.note}
        stroke={selected ? THEME.accent : THEME.noteEdge}
        strokeWidth={selected ? 1.5 : border}
        filter={`url(#${shadow})`}
      />
      {note.lines.map((line, index) => {
        const lineY = y + baseline + index * lineHeight;
        const left = x + inset + line.indent;
        return (
          <g key={index} data-line={index}>
            {line.marker && (
              <text x={left - 4} y={lineY} fontSize={fontSize} fill={THEME.ink} textAnchor="end">
                {line.marker}
              </text>
            )}
            {line.runs.length > 0 && (
              // Spaces between runs are part of the text, not layout.
              <text x={left} y={lineY} fontSize={fontSize} fill={THEME.ink} xmlSpace="preserve">
                {line.runs.map((run, part) => {
                  const decoration = [run.underline && "underline", run.strike && "line-through"].filter(Boolean).join(" ");
                  return (
                    <tspan
                      key={part}
                      fontWeight={run.bold ? "bold" : undefined}
                      fontStyle={run.italic ? "italic" : undefined}
                      textDecoration={decoration || undefined}
                    >
                      {run.text}
                    </tspan>
                  );
                })}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
});

/**
 * Each fluid's colour: in the condition set's order, so a fluid keeps its
 * colour from step to step (and a script's exported key holds on every
 * page), then any other the preview meets, in the order it meets them.
 */
export function fluidColours(preview: StepPreview | null | undefined): Map<string, string> {
  const colours = new Map<string, string>();
  const given = preview?.fluidColours ?? {};
  // A fluid given a colour is drawn in it; the rest by their place in the list.
  const add = (fluid: string | null) => {
    if (fluid && fluid !== AIR && !colours.has(fluid)) colours.set(fluid, given[fluid] ?? autoFluidColour(colours.size));
  };
  for (const fluid of Object.keys(preview?.fluidLabels ?? {})) add(fluid);
  for (const node of Object.values(preview?.nodes ?? {})) add(node.liquid);
  for (const spans of Object.values(preview?.edges ?? {})) for (const span of spans) add(span.liquid);
  return colours;
}

/** The colour the fluid at `index` in the condition set's list takes when it is given none. */
export function autoFluidColour(index: number): string {
  return THEME.previewFluids[index % THEME.previewFluids.length]!;
}

/** The colour a condition set's fluid is drawn in: its own, else its place's. */
export function fluidColourOf(fluids: readonly { name: string; colour?: string | null }[], index: number): string {
  const others = fluids.slice(0, index).filter((fluid) => fluid.name !== AIR).length;
  return fluids[index]?.colour ?? autoFluidColour(others);
}

/** The part of a polyline between two fractions of its drawn length. */
export function slicePath(path: readonly Point[], from: number, to: number): Point[] {
  const lengths = path.slice(1).map((point, index) => Math.hypot(point.x - path[index]!.x, point.y - path[index]!.y));
  const total = lengths.reduce((sum, length) => sum + length, 0);
  if (total === 0 || to <= from) return [];
  const at = (distance: number): Point => {
    let remaining = distance;
    for (let index = 0; index < lengths.length; index++) {
      const span = lengths[index]!;
      if (remaining <= span || index === lengths.length - 1) {
        const t = span > 0 ? Math.min(1, remaining / span) : 0;
        const a = path[index]!;
        const b = path[index + 1]!;
        return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
      }
      remaining -= span;
    }
    return path[path.length - 1]!;
  };
  const start = from * total;
  const end = to * total;
  const points: Point[] = [at(start)];
  let walked = 0;
  for (let index = 0; index < lengths.length; index++) {
    walked += lengths[index]!;
    if (walked > start && walked < end) points.push(path[index + 1]!);
  }
  points.push(at(end));
  return points;
}

/**
 * Under the lines: liquid in each stretch of line, in its fluid's colour, and
 * where there is none, a faint band for what the gas there is open to:
 * pressure or vacuum. Vented and sealed spaces are left plain.
 */
function PreviewBands({ scene }: { scene: Scene }) {
  const colours = fluidColours(scene.preview);
  const bands: ReactElement[] = [];
  for (const edge of scene.edges) {
    const spans = scene.preview?.edges[edge.ref] ?? [];
    spans.forEach((span, index) => {
      const colour = span.liquid
        ? colours.get(span.liquid)
        : span.gas === "pressure"
          ? THEME.previewPressure
          : span.gas === "vacuum"
            ? THEME.previewVacuum
            : undefined;
      if (!colour) return;
      const points = slicePath(edge.path, span.from, span.to);
      if (points.length < 2) return;
      bands.push(
        <polyline
          key={`${edge.ref}-${index}`}
          data-preview={edge.ref}
          data-preview-kind={span.liquid ? "liquid" : span.gas}
          points={points.map((point) => `${point.x},${point.y}`).join(" ")}
          fill="none"
          stroke={colour}
          strokeOpacity={span.liquid ? 0.55 : 0.3}
          strokeWidth={span.liquid ? 7 : 9}
          strokeLinecap="round"
          strokeLinejoin="round"
        />,
      );
    });
  }
  // Each stateful component washed green where it passes or runs, red where it
  // is shut or stopped: the step's states at a glance, under the symbol.
  for (const node of scene.nodes) {
    const state = scene.preview?.states[node.ref];
    const place = scene.preview?.nodes[node.ref];
    if (state === undefined || !place || node.role === "reservoir") continue;
    const blocked = blocks(node, state, place.passes);
    // A switching valve always passes: washed for the gas on the route its state picks.
    const colour = place.switched ? gasColour(place.switched.gas) : blocked ? THEME.previewClosed : THEME.previewOpen;
    bands.push(
      <g
        key={`state-${node.ref}`}
        data-preview-wash={node.ref}
        data-wash-state={place.switched ? place.switched.gas : blocked ? "closed" : "open"}
        transform={symbolTransform(node.centre, node.rotation, node.mirrored)}
      >
        <StateBacking node={node} rx={STATE_WASH} />
        <rect
          x={-node.size.width / 2 - STATE_WASH}
          y={-node.size.height / 2 - STATE_WASH}
          width={node.size.width + 2 * STATE_WASH}
          height={node.size.height + 2 * STATE_WASH}
          rx={STATE_WASH}
          fill={colour}
          fillOpacity={0.22}
          stroke={colour}
          strokeOpacity={0.45}
          strokeWidth={1}
        />
      </g>,
    );
  }
  // Each port washed in its status colour, as a valve is in its state's.
  for (const node of scene.nodes) {
    if (!scene.preview || !hasStatus(scene, node)) continue;
    const port = scene.preview.ports[node.ref];
    const inline = !port && scene.preview.inlinePorts?.includes(node.ref);
    const colour = portColour(inline ? "inline" : port?.kind);
    bands.push(
      <g key={`port-${node.ref}`} data-preview-wash={node.ref} data-wash-state={inline ? "inline" : (port?.kind ?? "sealed")} transform={symbolTransform(node.centre, node.rotation, node.mirrored)}>
        <StateBacking node={node} rx={STATE_WASH * 2} />
        <rect
          x={-node.size.width / 2 - STATE_WASH}
          y={-node.size.height / 2 - STATE_WASH}
          width={node.size.width + 2 * STATE_WASH}
          height={node.size.height + 2 * STATE_WASH}
          rx={STATE_WASH * 2}
          fill={colour}
          fillOpacity={0.22}
          stroke={colour}
          strokeOpacity={0.45}
          strokeWidth={1}
        />
      </g>,
    );
  }
  // A component holding liquid: a patch behind it in the fluid's colour. Not
  // one washed for its state or status: its colour is that, alone, so an open
  // valve reads green whatever flows through it.
  for (const node of scene.nodes) {
    const liquid = scene.preview?.nodes[node.ref]?.liquid;
    if (!liquid || stateWashed(scene, node)) continue;
    bands.push(
      <g key={`node-${node.ref}`} data-preview={node.ref} data-preview-kind="liquid" transform={symbolTransform(node.centre, node.rotation, node.mirrored)}>
        <rect
          x={-node.size.width / 2 - 4}
          y={-node.size.height / 2 - 4}
          width={node.size.width + 8}
          height={node.size.height + 8}
          rx={6}
          fill={colours.get(liquid)}
          fillOpacity={0.28}
        />
      </g>,
    );
  }
  // An empty vessel under pressure or vacuum: a faint patch in that colour, as a line's band.
  for (const node of scene.nodes) {
    const place = scene.preview?.nodes[node.ref];
    if (!place || place.liquid || !["reservoir", "chamber", "variable_volume_chamber"].includes(node.role)) continue;
    const colour = place.gas === "pressure" || place.gas === "flow" ? THEME.previewPressure : place.gas === "vacuum" ? THEME.previewVacuum : undefined;
    if (!colour) continue;
    bands.push(
      <g key={`gas-${node.ref}`} data-preview={node.ref} data-preview-kind={place.gas} transform={symbolTransform(node.centre, node.rotation, node.mirrored)}>
        <rect
          x={-node.size.width / 2 - 4}
          y={-node.size.height / 2 - 4}
          width={node.size.width + 8}
          height={node.size.height + 8}
          rx={6}
          fill={colour}
          fillOpacity={0.22}
        />
      </g>,
    );
  }
  return (
    <g data-layer="preview-bands" aria-hidden="true" pointerEvents="none">
      {bands}
    </g>
  );
}

/** A port, or a drawn supply with a source (its own, or its rating's): it has a status to show and set. */
function hasStatus(scene: Scene, node: SceneNode): boolean {
  return node.role === "port" || (node.role === "supply" && scene.preview?.ports[node.ref] !== undefined);
}

/** Washed for its state (a valve, a pump) or its status (a port), so drawn in that colour only. */
function stateWashed(scene: Scene, node: SceneNode): boolean {
  if (hasStatus(scene, node)) return true;
  return node.role !== "reservoir" && scene.preview?.states[node.ref] !== undefined && scene.preview.nodes[node.ref] !== undefined;
}

/**
 * Opaque under a state wash: the band of the line a valve sits on (pressure,
 * vacuum, a fluid) stops at its edge rather than tinting its green or red.
 */
function StateBacking({ node, rx }: { node: SceneNode; rx: number }) {
  return (
    <rect
      x={-node.size.width / 2 - STATE_WASH}
      y={-node.size.height / 2 - STATE_WASH}
      width={node.size.width + 2 * STATE_WASH}
      height={node.size.height + 2 * STATE_WASH}
      rx={rx}
      fill={THEME.surface}
    />
  );
}

/** Whether a preview is a script's step zero: the only one that says how much each bag holds. */
function isStepZero(preview: NonNullable<Scene["preview"]>): boolean {
  return preview.stepZero === true;
}

/** Room the open or closed wash leaves around a symbol, in scene units. */
const STATE_WASH = 6;

/**
 * Where a port's status reads: under its name, or where the name would be.
 * Shared with the canvas's in-place editor, so editing happens where it reads.
 */
export function portStatusAt(node: SceneNode, labelsShown: boolean): Point {
  const label = labelPlacement(node.centre, node.size, node.rotation, node.labelSide);
  const line = labelsShown && node.label ? THEME.fontSize + 6 : 0;
  // Away from the symbol: above a label that sits above it, else under the label.
  return { x: label.x, y: node.labelSide === "above" ? label.y - line : label.y + line };
}

/** A port's status colour: green open or vented, red sealed, orange pressure or flow, purple vacuum. */
export function portColour(kind: string | undefined): string {
  switch (kind) {
    case "open":
    case "inline":
      return THEME.previewOpen;
    case "pressure":
    case "flow":
      return THEME.previewPressure;
    case "vacuum":
      return THEME.previewVacuum;
    default:
      return THEME.previewClosed;
  }
}

/** The colour of what gas is open to: orange pressure or flow, purple vacuum, green vented, grey sealed. */
export function gasColour(gas: string): string {
  switch (gas) {
    case "pressure":
    case "flow":
      return THEME.previewPressure;
    case "vacuum":
      return THEME.previewVacuum;
    case "vented":
      return THEME.previewOpen;
    default:
      return THEME.muted;
  }
}

/** A filled chip with white text, anchored as the label is: what a port is set to, readable at a glance. */
function StatusChip({
  at,
  anchor,
  text,
  colour,
  outline,
}: {
  at: Point;
  anchor: "start" | "middle" | "end";
  text: string;
  colour: string;
  /** Drawn in outline rather than filled: a reading only bounded (solid edge) or not known (dashed). */
  outline?: "solid" | "dashed";
}) {
  const size = THEME.fontSize - 1;
  const width = text.length * size * 0.62 + 10;
  const height = size + 6;
  const left = anchor === "start" ? at.x : anchor === "end" ? at.x - width : at.x - width / 2;
  return (
    <>
      <rect
        x={left}
        y={at.y - size - 1}
        width={width}
        height={height}
        rx={height / 2}
        fill={outline ? THEME.surface : colour}
        stroke={outline ? colour : undefined}
        strokeWidth={outline ? 1.2 : undefined}
        strokeDasharray={outline === "dashed" ? "3 2" : undefined}
      />
      <text x={left + width / 2} y={at.y} textAnchor="middle" fontSize={size} fontFamily={THEME.monoFont} fontWeight={600} fill={outline ? colour : "#ffffff"}>
        {text}
      </text>
    </>
  );
}

/**
 * On top of the drawing, for the step shown: a click target over each
 * stateful component (its wash, green or red, is drawn underneath) that
 * moves it to its next state; each sourced port's status under its
 * name ("−60 kPa", "atm"), which a click edits; and in each bag what it
 * holds, with how much at step zero.
 */
function PreviewStates({ scene }: { scene: Scene }) {
  const preview = scene.preview!;
  const marks: ReactElement[] = [];
  const clip = useId();
  const colours = fluidColours(scene.preview);
  for (const node of scene.nodes) {
    const state = preview.states[node.ref];
    const place = preview.nodes[node.ref];
    const route = place?.switched ? switchedRoute(node.glyph, node.size, place.switched.port) : null;
    if (route && place?.switched) {
      // A switching valve's position: the one its state puts it in, in the
      // colour of the gas on that route, and the one it is not, faded.
      const colour = gasColour(place.switched.gas);
      marks.push(
        <g
          key={`route-${node.ref}`}
          data-preview-route={node.ref}
          data-route-port={place.switched.port}
          data-route-gas={place.switched.gas}
          transform={symbolTransform(node.centre, node.rotation, node.mirrored)}
          pointerEvents="none"
        >
          <rect {...route.idle} fill={THEME.surface} fillOpacity={0.6} />
          <rect {...route.active} fill={colour} fillOpacity={0.28} stroke={colour} strokeWidth={1.5} />
          {route.stubs.map((d) => (
            <path key={d} d={d} fill="none" stroke={colour} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
          ))}
        </g>,
      );
    }
    if (state !== undefined && place && node.role !== "reservoir") {
      // The whole symbol is the target: a click moves it to its next state.
      marks.push(
        <g
          key={`state-${node.ref}`}
          data-kind="state-toggle"
          data-ref={node.ref}
          data-preview-state={node.ref}
          data-passes={place.passes}
          data-state={state}
          transform={symbolTransform(node.centre, node.rotation, node.mirrored)}
          pointerEvents="all"
          style={{ cursor: "pointer" }}
        >
          <title>{`${node.label || node.ref}: ${state}. Click to change`}</title>
          <rect
            x={-node.size.width / 2 - STATE_WASH}
            y={-node.size.height / 2 - STATE_WASH}
            width={node.size.width + 2 * STATE_WASH}
            height={node.size.height + 2 * STATE_WASH}
            fill="transparent"
          />
        </g>,
      );
    }
    if (hasStatus(scene, node)) {
      const port = preview.ports[node.ref];
      // Joined on both sides and unsourced: a connection, open, with nothing to set.
      const inline = !port && preview.inlinePorts?.includes(node.ref);
      const at = portStatusAt(node, scene.annotations.labels);
      const anchor = labelPlacement(node.centre, node.size, node.rotation, node.labelSide).anchor;
      marks.push(
        <g
          key={`port-${node.ref}`}
          data-kind={inline ? undefined : "port-setting"}
          data-port-inline={inline ? node.ref : undefined}
          data-port-status={inline ? "inline" : (port?.kind ?? "sealed")}
          data-ref={node.ref}
          pointerEvents="all"
          style={inline ? undefined : { cursor: "pointer" }}
        >
          <title>
            {inline
              ? `${node.label || node.ref}: open, a connection between two lines`
              : `${node.label || node.ref}: ${port?.unsourced ? "open to atmosphere, no source" : port?.rated ? `at its rating, ${portText(port, scene.units)}: the condition set gives it none` : port ? `${port.source} ${port.setting}` : "sealed, no source yet"}. Click to change`}
          </title>
          <StatusChip at={at} anchor={anchor} text={inline ? "open" : portText(port, scene.units)} colour={portColour(inline ? "inline" : port?.kind)} />
        </g>,
      );
    }
    const regulator = node.role === "regulator" ? preview.regulators?.[node.ref] : undefined;
    if (regulator) {
      // What it is asked to hold, where a port's status would be: set here, unless the step sweeps it.
      const text = regulatorText(regulator, scene.units);
      const swept = regulator.sweep !== undefined;
      marks.push(
        <g
          key={`regulator-${node.ref}`}
          data-kind={swept ? undefined : "regulator-setting"}
          data-ref={node.ref}
          data-regulator-setpoint={text}
          pointerEvents="all"
          style={swept ? undefined : { cursor: "pointer" }}
        >
          <title>
            {`${node.label || node.ref}: ${text === "off" ? "no setpoint, its outlet relieved" : `set to ${text}`}. ${swept ? "Swept in this step: change it in the step" : "Click to change"}`}
          </title>
          <StatusChip
            at={portStatusAt(node, scene.annotations.labels)}
            anchor={labelPlacement(node.centre, node.size, node.rotation, node.labelSide).anchor}
            text={`${instrumentTag({ local_id: node.ref, label: node.label }, "PIC")} ${text}`}
            colour={gasColour(regulator.gas)}
          />
        </g>,
      );
    }
    const readout = node.role === "sensor" ? preview.readouts?.[node.ref] : undefined;
    if (readout) {
      // What it reads, where a port's status would be: filled where the preview knows it, outlined where it only bounds it, dashed where it cannot say.
      const text = readoutText(readout, scene.units);
      const limit = limitText(readout, scene.units);
      const value = readout.value ?? readout.high ?? 0;
      const colour =
        readout.certainty === "unknown"
          ? THEME.muted
          : readout.state === "liquid"
            ? (colours.get(preview.nodes[node.ref]?.liquid ?? "") ?? THEME.previewFluids[0]!)
            : readout.observes === "pressure" && value > 0
              ? THEME.previewPressure
              : readout.observes === "pressure" && (readout.value ?? readout.low ?? 0) < 0
                ? THEME.previewVacuum
                : readout.observes === "flow_rate" && value > 0
                  ? THEME.previewOpen
                  : THEME.ink;
      const at = portStatusAt(node, scene.annotations.labels);
      const anchor = labelPlacement(node.centre, node.size, node.rotation, node.labelSide).anchor;
      marks.push(
        <g
          key={`readout-${node.ref}`}
          data-readout={node.ref}
          data-readout-certainty={readout.certainty}
          data-readout-text={text}
          data-readout-limit={limit ?? undefined}
          data-readout-unreachable={readout.limit?.unreachable || undefined}
          pointerEvents="all"
        >
          <title>
            {`${readout.tag} ${node.label || node.ref}: ${text}${
              readout.certainty === "bound" ? ", as far as the preview can say while it flows" : readout.certainty === "unknown" ? ", which the preview cannot say" : ""
            }${limit ? `. The step ends at ${limit}${readout.limit?.unreachable ? ", which it can never reach" : ""}` : ""}`}
          </title>
          <StatusChip
            at={at}
            anchor={anchor}
            text={`${readout.tag} ${text}`}
            colour={colour}
            {...(readout.certainty === "bound" ? { outline: "solid" as const } : readout.certainty === "unknown" ? { outline: "dashed" as const } : {})}
          />
          {limit && (
            <text
              x={at.x}
              y={at.y + THEME.fontSize + 4}
              textAnchor={anchor}
              fontSize={THEME.fontSize - 2}
              fontFamily={THEME.monoFont}
              fill={readout.limit?.unreachable ? THEME.previewClosed : THEME.muted}
            >
              {`limit ${limit}`}
            </text>
          )}
        </g>,
      );
    }
    const operation = preview.operations?.[node.ref];
    if (node.role === "pump" && operation) {
      // What it does in this step, where a port's status would be.
      marks.push(
        <g key={`pump-${node.ref}`} data-pump-operation={node.ref} pointerEvents="none">
          <StatusChip
            at={portStatusAt(node, scene.annotations.labels)}
            anchor={labelPlacement(node.centre, node.size, node.rotation, node.labelSide).anchor}
            text={operation}
            colour={operation === "off" ? THEME.previewClosed : THEME.previewOpen}
          />
        </g>,
      );
    }
    if (node.role === "reservoir") {
      const liquid = place?.liquid ?? null;
      const volume = preview.volumes[node.ref];
      // At step zero, a script's own: what the bag holds is set here, so an empty one says so.
      const settable = preview.inherited !== true && isStepZero(preview);
      const lines = [
        liquid ? (preview.fluidLabels[liquid] ?? liquid) : settable ? "empty" : null,
        // As set at step zero; after a step nominal, to the whole unit (D60).
        volume !== undefined ? (isStepZero(preview) ? volumeLabel(volume, scene.units) : nominalVolumeLabel(volume, scene.units)) : null,
      ].filter((line): line is string => line !== null);
      const size = Math.min(9, Math.max(6, node.size.width / 6));
      const first = node.centre.y - ((lines.length - 1) * (size + 1)) / 2 + size / 3;
      // Filled to its nominal level: what it holds over what it holds full, a
      // free surface across its own outline. Nominal, not simulated (D61).
      const capacity = preview.capacities?.[node.ref];
      if (liquid && volume !== undefined && capacity) {
        const fraction = Math.min(1, Math.max(0, volume / capacity));
        const h = node.size.height / 2;
        const surface = h - fraction * node.size.height;
        const colour = colours.get(liquid) ?? THEME.previewFluids[0]!;
        const id = `${clip}-fill-${node.ref}`;
        marks.push(
          <g key={`fill-${node.ref}`} data-preview-fill={node.ref} data-fill-fraction={fraction.toFixed(3)} transform={symbolTransform(node.centre, node.rotation, node.mirrored)} pointerEvents="none">
            <clipPath id={id}>
              <Glyph name={node.glyph} size={node.size} mirrored={node.mirrored} />
            </clipPath>
            <g clipPath={`url(#${id})`}>
              <rect x={-node.size.width / 2} y={surface} width={node.size.width} height={h - surface} fill={colour} fillOpacity={0.45} />
              {fraction > 0 && fraction < 1 && <line x1={-node.size.width / 2} y1={surface} x2={node.size.width / 2} y2={surface} stroke={colour} strokeWidth={1.2} />}
            </g>
          </g>,
        );
      }
      marks.push(
        <g
          key={`bag-${node.ref}`}
          data-preview-bag={node.ref}
          data-kind={settable ? "bag-setting" : undefined}
          data-ref={settable ? node.ref : undefined}
          pointerEvents={settable ? "all" : "none"}
          style={settable ? { cursor: "pointer" } : undefined}
        >
          {settable && (
            <rect
              x={node.centre.x - node.size.width / 2}
              y={node.centre.y - node.size.height / 2}
              width={node.size.width}
              height={node.size.height}
              fill="transparent"
            >
              <title>{`${node.label || node.ref}: what it holds at the start. Click to change`}</title>
            </rect>
          )}
          {lines.length > 0 && (
            <text x={node.centre.x} textAnchor="middle" fontSize={size} fill={liquid ? THEME.ink : THEME.muted} pointerEvents="none">
              {lines.map((line, index) => (
                <tspan key={index} x={node.centre.x} y={first + index * (size + 1)} fontWeight={index === 0 && liquid ? 600 : undefined}>
                  {line.length > 14 ? `${line.slice(0, 13)}…` : line}
                </tspan>
              ))}
            </text>
          )}
        </g>,
      );
    }
  }
  return (
    <g data-layer="preview-states" pointerEvents="none">
      {marks}
    </g>
  );
}

/** Room the wash leaves around a symbol, in scene units. */
const WASH_PADDING = 8;

/**
 * A soft band under each washed line and a rounded patch behind each washed
 * component or collapsed block. Per element rather than one region, so it
 * never spills onto the other side.
 */
function Wash({ scene }: { scene: Scene }) {
  const fill = scene.annotations.wash === "inside" ? THEME.washInside : THEME.washOutside;
  const patch = (key: string, centre: Point, size: Size, rotation: number, mirrored: boolean) => (
    <g key={key} data-wash={key} transform={symbolTransform(centre, rotation, mirrored)}>
      <rect
        x={-size.width / 2 - WASH_PADDING}
        y={-size.height / 2 - WASH_PADDING}
        width={size.width + 2 * WASH_PADDING}
        height={size.height + 2 * WASH_PADDING}
        rx={WASH_PADDING}
        fill={fill}
      />
    </g>
  );
  return (
    <g data-layer="wash" data-wash-side={scene.annotations.wash} aria-hidden="true" pointerEvents="none">
      {scene.edges
        .filter((edge) => scene.washed.has(edge.ref))
        .map((edge) => (
          <polyline
            key={edge.ref}
            data-wash={edge.ref}
            points={edge.path.map((point) => `${point.x},${point.y}`).join(" ")}
            fill="none"
            stroke={fill}
            strokeWidth={THEME.edgeWidth + 2 * WASH_PADDING}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
      {scene.tubeRuns.map((run) => (
        <line
          key={`run-${run.ref}`}
          data-wash={`run-${run.ref}`}
          x1={run.from.x}
          y1={run.from.y}
          x2={run.to.x}
          y2={run.to.y}
          stroke={fill}
          strokeWidth={THEME.edgeWidth + 2 * WASH_PADDING}
          strokeLinecap="round"
        />
      ))}
      {scene.nodes
        .filter((node) => scene.washed.has(node.ref))
        .map((node) => patch(node.ref, node.centre, node.size, node.rotation, node.mirrored))}
      {scene.blocks
        .filter((block) => block.collapsed && scene.washed.has(block.ref))
        .map((block) => patch(block.ref, block.centre, block.size, block.rotation, block.mirrored))}
    </g>
  );
}

/**
 * The highlighted refs among some ports, as a string so memoised elements
 * compare it by value: highlighting one port re-renders one node.
 */
function connectedAmong(ports: readonly ScenePort[], connected: ReadonlySet<string>): string {
  return ports
    .filter((port) => connected.has(port.ref))
    .map((port) => port.ref)
    .join(" ");
}

function highlightedAmong(ports: readonly ScenePort[], highlighted: ReadonlySet<string>): string {
  if (highlighted.size === 0) {
    return "";
  }
  return ports
    .filter((port) => highlighted.has(port.ref))
    .map((port) => port.ref)
    .join(" ");
}

// --- Nodes ------------------------------------------------------------------

/** Half the extent of a rotated box, for placing things just outside it. */
export function halfExtent(size: Size, rotation: number): Size {
  const cos = Math.abs(Math.cos(rotation));
  const sin = Math.abs(Math.sin(rotation));
  return {
    width: (size.width * cos + size.height * sin) / 2,
    height: (size.width * sin + size.height * cos) / 2,
  };
}

/**
 * The symbol transform: translate to the centre, turn, and mirror — which SVG
 * applies right to left, so the glyph is mirrored in its own frame first, then
 * turned, exactly as the scene builder places its ports.
 */
export function symbolTransform(centre: Point, rotation: number, mirrored: boolean): string {
  const rotate = rotation === 0 ? "" : ` rotate(${toDegrees(rotation)})`;
  const mirror = mirrored ? " scale(-1 1)" : "";
  return `translate(${centre.x} ${centre.y})${rotate}${mirror}`;
}

/**
 * A node's fluidic port positions in its symbol's own frame: the scene's
 * positions taken back through `symbolTransform` (translate, rotate, mirror).
 * A junction draws its stubs to them, so the stubs meet the ports whichever
 * side a template puts its branch on, and however the node is turned.
 */
export function localPorts(node: SceneNode): { x: number; y: number }[] {
  const cos = Math.cos(-node.rotation);
  const sin = Math.sin(-node.rotation);
  return node.ports
    .filter((port) => port.kind === "fluidic")
    .map(({ position }) => {
      const dx = position.x - node.centre.x;
      const dy = position.y - node.centre.y;
      const x = dx * cos - dy * sin;
      const y = dx * sin + dy * cos;
      return { x: Math.round((node.mirrored ? -x : x) * 1000) / 1000, y: Math.round(y * 1000) / 1000 };
    });
}

function toDegrees(radians: number): number {
  return Math.round(((radians * 180) / Math.PI) * 1e6) / 1e6;
}

function describeFindings(element: Badged): string {
  if (element.findings.length === 0) {
    return "";
  }
  const counts = new Map<Severity, number>();
  for (const finding of element.findings) {
    counts.set(finding.severity, (counts.get(finding.severity) ?? 0) + 1);
  }
  const parts = (["error", "warning", "info"] as const)
    .filter((severity) => counts.has(severity))
    .map((severity) => {
      const count = counts.get(severity)!;
      return `${count} ${SEVERITY_STYLE[severity].label}${count === 1 ? "" : "s"}`;
    });
  return `, ${parts.join(", ")}`;
}

const NodeView = memo(function NodeView({
  node,
  selected,
  highlighted,
  showLabel,
  quiet,
  filled,
}: {
  node: SceneNode;
  selected: boolean;
  /** Showing its fill level in a script preview (D61). */
  filled: boolean;
  highlighted: string;
  /** Scene annotations: whether component labels are drawn. */
  showLabel: boolean;
  /**
   * Ports drawn without their circle, space-separated: a junction's connected
   * ports, so its lines run into the dot (D32). Still ports for hit-testing.
   */
  quiet: string;
}) {
  const { centre, size, rotation } = node;
  const extent = halfExtent(size, rotation);
  const highlightedRefs = new Set(highlighted ? highlighted.split(" ") : []);
  const quietRefs = new Set(quiet ? quiet.split(" ") : []);
  const label = labelPlacement(centre, size, rotation, node.labelSide);

  return (
    <g
      data-kind="node"
      data-ref={node.ref}
      data-role={node.role}
      data-selected={selected || undefined}
      data-stray={node.strayIn}
      opacity={node.strayIn && !selected ? 0.4 : undefined}
      role="img"
      aria-label={`${node.role.replace(/_/g, " ")} ${node.label || node.ref}${node.strayIn ? `, not part of ${node.strayIn}` : ""}${describeFindings(node)}`}
    >
      <title>
        {hoverHint([
          node.label && node.label !== node.ref ? `${node.label} (${node.ref})` : node.ref,
          roleText(node.role),
          node.strayIn && `not part of ${node.strayIn}`,
        ])}
      </title>
      <g transform={symbolTransform(centre, rotation, node.mirrored)} data-mirrored={node.mirrored || undefined}>
        {/* The whole symbol box is a target, not just its strokes: a pinch
            valve or a clamp is mostly empty space between lines. Transparent
            rather than "none", which would not catch the pointer. */}
        <rect
          data-hit="node"
          x={-size.width / 2}
          y={-size.height / 2}
          width={size.width}
          height={size.height}
          fill="transparent"
        />
        <Glyph name={node.glyph} size={size} ports={node.glyph === "junction" ? localPorts(node) : undefined} filled={filled} mirrored={node.mirrored} />
        {selected && <SelectionOutline size={size} />}
      </g>
      {showLabel && node.label && (
      <text
        data-label="node"
        data-side={node.labelSide}
        x={label.x}
        y={label.y}
        textAnchor={label.anchor}
        fontSize={THEME.fontSize}
        // The selected component's name picks up the selection colour.
        fontWeight={selected ? 600 : undefined}
        fill={selected ? THEME.accent : THEME.ink}
      >
        {node.label}
      </text>
      )}
      {node.ports.map((port) =>
        quietRefs.has(port.ref) && !highlightedRefs.has(port.ref) ? null : (
          <PortView key={port.ref} port={port} highlighted={highlightedRefs.has(port.ref)} />
        ),
      )}
      <FindingsBadge
        element={node}
        owner={node.ref}
        at={{ x: centre.x + extent.width, y: centre.y - extent.height }}
      />
    </g>
  );
});

function SelectionOutline({ size, padding = 4 }: { size: Size; padding?: number }) {
  return (
    <rect
      data-selection="outline"
      x={-size.width / 2 - padding}
      y={-size.height / 2 - padding}
      width={size.width + 2 * padding}
      height={size.height + 2 * padding}
      rx={3}
      fill="none"
      stroke={THEME.accent}
      strokeWidth={1.5}
      strokeDasharray="4 2"
    />
  );
}

function PortView({ port, highlighted }: { port: ScenePort; highlighted: boolean }) {
  const { x, y } = port.position;
  const r = THEME.portRadius;
  const fill = port.isExternal ? THEME.ink : THEME.surface;
  const stroke = port.worstSeverity ? SEVERITY_STYLE[port.worstSeverity].fill : THEME.ink;
  return (
    <g
      data-kind="port"
      data-ref={port.ref}
      data-port-kind={port.kind}
      data-highlighted={highlighted || undefined}
    >
      {/* The port and the element it belongs to: "Inlet · valve_1.port_a". */}
      <title>{hoverHint([port.name, port.ref])}</title>
      {highlighted && (
        <circle cx={x} cy={y} r={r + 3.5} fill="none" stroke={THEME.accent} strokeWidth={2} />
      )}
      {port.deadEnd ? (
        // Closed on purpose: an x where the port would be. A transparent disc
        // under it keeps the port as easy to hit as any other.
        <g data-dead-end="true">
          <circle cx={x} cy={y} r={r + 1} fill="transparent" />
          <path
            d={`M${x - r} ${y - r}L${x + r} ${y + r}M${x - r} ${y + r}L${x + r} ${y - r}`}
            stroke={stroke}
            strokeWidth={1.75}
            strokeLinecap="round"
          />
        </g>
      ) : port.kind === "instrument" ? (
        // Instrument connections are square, so they read differently from
        // fluidic ports without relying on colour.
        <rect x={x - r} y={y - r} width={2 * r} height={2 * r} fill={fill} stroke={stroke} strokeWidth={1.25} />
      ) : (
        <circle cx={x} cy={y} r={r} fill={fill} stroke={stroke} strokeWidth={1.25} />
      )}
    </g>
  );
}

// --- Edges ------------------------------------------------------------------

/** Rough label width, for placing marks and badges beside it. */
function approximateTextWidth(text: string, fontSize: number): number {
  // Generous: overestimating only adds a gap, underestimating overlaps.
  return text.length * fontSize * 0.62;
}

/**
 * A glow under the given lines and components, so they stand out of the
 * drawing: under the lines and symbols, which draw over it unchanged.
 */
function Spotlight({ scene, refs }: { scene: Scene; refs: ReadonlySet<string> }) {
  const pad = 6;
  return (
    <g data-layer="spotlight" pointerEvents="none">
      {scene.edges
        .filter((edge) => refs.has(edge.ref))
        .map((edge) => (
          <polyline
            key={edge.ref}
            data-spotlight={edge.ref}
            points={edge.path.map((point) => `${point.x},${point.y}`).join(" ")}
            fill="none"
            stroke={THEME.accent}
            strokeOpacity={0.4}
            strokeWidth={THEME.edgeWidth + 10}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
      {scene.nodes
        .filter((node) => refs.has(node.ref))
        .map((node) => {
          const extent = halfExtent(node.size, node.rotation);
          return (
            <rect
              key={node.ref}
              data-spotlight={node.ref}
              x={node.centre.x - extent.width - pad}
              y={node.centre.y - extent.height - pad}
              width={2 * (extent.width + pad)}
              height={2 * (extent.height + pad)}
              rx={6}
              fill={THEME.accent}
              fillOpacity={0.22}
              stroke={THEME.accent}
              strokeWidth={2}
            />
          );
        })}
    </g>
  );
}

/** "valve 3" from "valve", for a hover hint. */
function roleText(role: string): string {
  const words = role.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * What the pointer is over, as the browser's hover hint: the nearest <title>
 * wins, so without one each element showed the whole schematic's name.
 */
function hoverHint(parts: readonly (string | null | undefined | false)[]): string {
  return parts.filter(Boolean).join(" · ");
}

const EdgeView = memo(function EdgeView({
  edge,
  selected,
  showDimension,
}: {
  edge: SceneEdge;
  selected: boolean;
  /** Scene annotations: whether the channel's length is drawn. */
  showDimension: boolean;
}) {
  const points = edge.path.map((point) => `${point.x},${point.y}`).join(" ");
  const concealed = edge.from.concealed || edge.to.concealed;
  // A finding is marked by its badge; the line keeps its colour, so a
  // design full of early findings still reads as a circuit.
  const stroke = THEME.channel;
  const label = showDimension ? edge.lengthLabel : null;
  const mark = edge.mixing ? "mixing" : edge.serpentine ? "serpentine" : null;
  const { x, y } = edge.labelPosition;
  const labelY = y - 6;
  const textWidth = label ? approximateTextWidth(label, THEME.labelFontSize) : 0;
  const markWidth = mark ? 16 : 0;
  // Mark then text, centred together on the label position.
  const startX = x - (markWidth + textWidth) / 2;

  return (
    <g
      data-kind="edge"
      data-ref={edge.ref}
      data-edge-type={edge.type}
      data-selected={selected || undefined}
      role="img"
      aria-label={`${edge.type.replace(/_/g, " ")} ${edge.ref}${label ? `, ${label}` : ""}${describeFindings(edge)}`}
    >
      <title>{hoverHint([edge.ref, roleText(edge.type), edge.lengthLabel])}</title>
      {/*
        The hit area: an invisible, wider stroke along the whole path, so a
        click anywhere on the line finds it, not just on its label. Constant
        in screen pixels whatever the zoom. Drawn first, so the visible line
        sits on top; nodes are a later layer, so a click near a symbol still
        goes to the symbol. Carries data-hit so an export can leave it out.
      */}
      <polyline
        data-hit="edge"
        points={points}
        fill="none"
        stroke="#000"
        strokeOpacity={0}
        strokeWidth={HIT_WIDTH}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        pointerEvents="stroke"
      />
      {selected && (
        <polyline
          data-selection="outline"
          points={points}
          fill="none"
          stroke={THEME.accent}
          strokeOpacity={0.35}
          strokeWidth={THEME.edgeWidth + 5}
          strokeLinejoin="round"
        />
      )}
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth={THEME.edgeWidth}
        strokeLinejoin="round"
        // A concealed end runs into a collapsed block through a port it does
        // not expose; dashing says the connection is real but not shown. An
        // air line is dotted (D30): round dots, so it never reads as dashed.
        strokeDasharray={edge.air ? (concealed ? "0.1 3.5 5 3.5" : "0.1 4") : concealed ? "5 3" : undefined}
        strokeLinecap={edge.air ? "round" : undefined}
        data-air={edge.air || undefined}
      />
      {mark === "serpentine" && <SerpentineMark x={startX + 7} y={labelY} />}
      {mark === "mixing" && <MixingMark x={startX + 7} y={labelY} />}
      {label && (
        <text
          x={startX + markWidth}
          y={labelY}
          dominantBaseline="central"
          fontSize={THEME.labelFontSize}
          fill={THEME.ink}
          stroke={THEME.surface}
          strokeWidth={3}
          paintOrder="stroke"
          data-label="length"
        >
          {label}
        </text>
      )}
      <FindingsBadge
        element={edge}
        owner={edge.ref}
        at={{ x: startX + markWidth + textWidth + THEME.badgeRadius + 4, y: labelY }}
      />
    </g>
  );
});

// --- Blocks -----------------------------------------------------------------

const BlockFrame = memo(function BlockFrame({ block, selected }: { block: SceneBlock; selected: boolean }) {
  const { centre, size } = block;
  const left = centre.x - size.width / 2;
  const top = centre.y - size.height / 2;
  return (
    <g data-kind="block" data-ref={block.ref} data-collapsed="false" data-selected={selected || undefined}>
      <title>{hoverHint([`${block.name} (${block.ref})`, "Block"])}</title>
      <rect
        x={left}
        y={top}
        width={size.width}
        height={size.height}
        rx={6}
        fill="none"
        stroke={selected ? THEME.accent : THEME.frame}
        strokeWidth={selected ? 2 : 1}
        strokeDasharray="6 4"
      />
      <text
        data-label="block"
        x={left + 6}
        y={top - 5}
        fontSize={THEME.fontSize}
        fontWeight={selected ? 600 : undefined}
        fill={selected ? THEME.accent : THEME.muted}
      >
        {block.name}
      </text>
      <FindingsBadge element={block} owner={block.ref} at={{ x: left + size.width, y: top }} />
    </g>
  );
});

const CollapsedBlockView = memo(function CollapsedBlockView({
  block,
  selected,
  highlighted,
}: {
  block: SceneBlock;
  selected: boolean;
  highlighted: string;
}) {
  const { centre, size, rotation } = block;
  const extent = halfExtent(size, rotation);
  const highlightedRefs = new Set(highlighted ? highlighted.split(" ") : []);
  return (
    <g
      data-kind="block"
      data-ref={block.ref}
      data-collapsed="true"
      data-selected={selected || undefined}
      role="img"
      aria-label={`block ${block.name} (${block.ref})${describeFindings(block)}`}
    >
      <title>{hoverHint([`${block.name} (${block.ref})`, "Block, collapsed"])}</title>
      <g transform={symbolTransform(centre, rotation, block.mirrored)} data-mirrored={block.mirrored || undefined}>
        <Glyph name={block.glyph} size={size} mirrored={block.mirrored} />
        {selected && <SelectionOutline size={size} />}
      </g>
      {/* One name, where the expanded frame shows it: above the symbol's
          left edge. Outside the turned group, so it never reads sideways. */}
      <text
        data-label="block"
        x={centre.x - extent.width + 2}
        y={centre.y - extent.height - 5}
        fontSize={THEME.fontSize}
        fontWeight={selected ? 600 : undefined}
        fill={selected ? THEME.accent : THEME.muted}
      >
        {block.name}
      </text>
      {block.ports.map((port) => (
        <PortView key={port.ref} port={port} highlighted={highlightedRefs.has(port.ref)} />
      ))}
      <FindingsBadge
        element={block}
        owner={block.ref}
        at={{ x: centre.x + extent.width, y: centre.y - extent.height }}
      />
    </g>
  );
});

// --- Findings badges --------------------------------------------------------

/**
 * A marker on an element that carries findings (requirement 12.9). Its title
 * lists them, and `data-kind="badge"` lets the canvas open the findings for
 * `owner` when the marker is selected.
 *
 * Severity is carried by shape as well as colour: a circle for errors, a
 * triangle for warnings, a square for information.
 */
function FindingsBadge({ element, owner, at }: { element: Badged; owner: string; at: Point }) {
  const severity = element.worstSeverity;
  if (severity === null) {
    return null;
  }
  const style = SEVERITY_STYLE[severity];
  const r = THEME.badgeRadius - 1;
  const count = element.findings.length;
  const { x, y } = at;
  return (
    <g data-kind="badge" data-ref={owner} data-severity={severity}>
      <title>{element.findings.map((finding) => finding.message).join("\n")}</title>
      {severity === "error" && <circle cx={x} cy={y} r={r} fill={style.fill} />}
      {severity === "warning" && (
        <path d={`M ${x} ${y - r - 1} L ${x + r + 1} ${y + r} L ${x - r - 1} ${y + r} Z`} fill={style.fill} />
      )}
      {severity === "info" && <rect x={x - r} y={y - r} width={2 * r} height={2 * r} rx={1.5} fill={style.fill} />}
      <text
        x={x}
        y={y + (severity === "warning" ? 1.5 : 0)}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={8}
        fontWeight="bold"
        fill={THEME.surface}
      >
        {count > 1 ? String(count) : style.mark}
      </text>
    </g>
  );
}
